"use client"

import { useState } from "react"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { Calendar, Download, Filter, RefreshCw } from "react-feather"
import Card from "./ui/Card"
import Button from "./ui/Button"
import "../styles/Analytics.css"

// Mock data
const monthlyData = [
  { month: "Jan", production: 1250, consumption: 980, efficiency: 72 },
  { month: "Feb", production: 1580, consumption: 1120, efficiency: 75 },
  { month: "Mar", production: 2120, consumption: 1450, efficiency: 82 },
  { month: "Apr", production: 2540, consumption: 1680, efficiency: 85 },
  { month: "May", production: 2980, consumption: 1890, efficiency: 88 },
  { month: "Jun", production: 3250, consumption: 2100, efficiency: 92 },
  { month: "Jul", production: 3420, consumption: 2240, efficiency: 94 },
  { month: "Aug", production: 3280, consumption: 2180, efficiency: 91 },
  { month: "Sep", production: 2850, consumption: 1920, efficiency: 87 },
  { month: "Oct", production: 2320, consumption: 1650, efficiency: 83 },
  { month: "Nov", production: 1780, consumption: 1320, efficiency: 78 },
  { month: "Dec", production: 1350, consumption: 1050, efficiency: 74 },
]

const yearlyData = [
  { year: 2019, production: 25600, consumption: 18900 },
  { year: 2020, production: 27800, consumption: 19500 },
  { year: 2021, production: 29400, consumption: 20100 },
  { year: 2022, production: 31200, consumption: 21300 },
  { year: 2023, production: 33500, consumption: 22800 },
]

const panelEfficiencyData = [
  { name: "Panel A", value: 94 },
  { name: "Panel B", value: 92 },
  { name: "Panel C", value: 89 },
  { name: "Panel D", value: 91 },
]

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"]

const energySourceData = [
  { name: "Solar", value: 68 },
  { name: "Grid", value: 32 },
]

const SOURCE_COLORS = ["#f59e0b", "#3b82f6"]

function Analytics() {
  const [timeRange, setTimeRange] = useState("year")
  const [year, setYear] = useState(2023)

  return (
    <div className="analytics-page">
      <div className="page-header">
        <h1>Analytics</h1>
        <div className="header-actions">
          <div className="time-range-selector">
            <button
              className={`time-range-btn ${timeRange === "month" ? "active" : ""}`}
              onClick={() => setTimeRange("month")}
            >
              Month
            </button>
            <button
              className={`time-range-btn ${timeRange === "quarter" ? "active" : ""}`}
              onClick={() => setTimeRange("quarter")}
            >
              Quarter
            </button>
            <button
              className={`time-range-btn ${timeRange === "year" ? "active" : ""}`}
              onClick={() => setTimeRange("year")}
            >
              Year
            </button>
            <button
              className={`time-range-btn ${timeRange === "custom" ? "active" : ""}`}
              onClick={() => setTimeRange("custom")}
            >
              Custom
            </button>
          </div>
          <Button variant="outline">
            <Calendar size={16} />
            <span>Date Range</span>
          </Button>
          <Button variant="outline">
            <Filter size={16} />
            <span>Filter</span>
          </Button>
          <Button variant="outline">
            <Download size={16} />
            <span>Export</span>
          </Button>
          <Button variant="icon">
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      <div className="year-selector">
        <button className="year-nav-btn" onClick={() => setYear(year - 1)} disabled={year <= 2019}>
          &lt;
        </button>
        <span className="current-year">{year}</span>
        <button className="year-nav-btn" onClick={() => setYear(year + 1)} disabled={year >= 2023}>
          &gt;
        </button>
      </div>

      <div className="analytics-summary">
        <Card className="summary-card">
          <div className="summary-header">
            <h3>Total Production</h3>
          </div>
          <div className="summary-content">
            <div className="summary-value">33,500 kWh</div>
            <div className="summary-change positive">+7.4% from previous year</div>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="summary-header">
            <h3>Total Consumption</h3>
          </div>
          <div className="summary-content">
            <div className="summary-value">22,800 kWh</div>
            <div className="summary-change positive">+7.0% from previous year</div>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="summary-header">
            <h3>Grid Export</h3>
          </div>
          <div className="summary-content">
            <div className="summary-value">10,700 kWh</div>
            <div className="summary-change positive">+8.1% from previous year</div>
          </div>
        </Card>
        <Card className="summary-card">
          <div className="summary-header">
            <h3>CO₂ Saved</h3>
          </div>
          <div className="summary-content">
            <div className="summary-value">16.8 tons</div>
            <div className="summary-change positive">+7.4% from previous year</div>
          </div>
        </Card>
      </div>

      <div className="analytics-charts">
        <Card className="chart-card full-width">
          <div className="card-header">
            <h3>Annual Energy Production & Consumption</h3>
            <p className="card-description">
              Monthly breakdown of energy production and consumption in kilowatt-hours (kWh)
            </p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="production" name="Production" fill="#f59e0b" />
                <Bar dataKey="consumption" name="Consumption" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="chart-card">
          <div className="card-header">
            <h3>5-Year Trend</h3>
            <p className="card-description">Annual energy production and consumption trends</p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={yearlyData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="production"
                  name="Production"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  activeDot={{ r: 8 }}
                />
                <Line type="monotone" dataKey="consumption" name="Consumption" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="chart-card">
          <div className="card-header">
            <h3>Panel Efficiency</h3>
            <p className="card-description">Efficiency comparison between different panel groups</p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={panelEfficiencyData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {panelEfficiencyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="chart-card">
          <div className="card-header">
            <h3>Energy Source Distribution</h3>
            <p className="card-description">Percentage of energy from solar vs. grid</p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={energySourceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}%`}
                >
                  {energySourceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="analytics-details">
        <Card className="detail-card full-width">
          <div className="card-header">
            <h3>Monthly Efficiency Analysis</h3>
            <p className="card-description">System efficiency throughout the year</p>
          </div>
          <div className="card-content chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis domain={[60, 100]} />
                <Tooltip formatter={(value) => `${value}%`} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="efficiency"
                  name="Efficiency"
                  stroke="#10b981"
                  strokeWidth={2}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default Analytics

